<template>
  <div v-if="review" ref="reviewContainer" class="isolate h-full w-full">
    <ReviewCard
      ref="reviewCard"
      :card-class="styles.cardClass"
      :defer
      :image-attributes="{
        decoding: 'async',
        loading: 'lazy',
        class: cardClassName ?? '',
        sizes: isInMainContainer ? MAIN_IMG_SIZES : ALT_IMG_SIZES_LARGE,
      }"
      :overlay-class="styles.defaultOverlayClass"
      :review
      :review-picture-class="styles.reviewCardPicture"
      @click="trackReviewClick"
    >
      <template #card-header>
        <RevTag
          class="absolute left-20 top-20"
          :label="reviewDate"
          variant="secondary"
        />
      </template>

      <template #card-body="{ openModal }">
        <div
          class="mood-inverse absolute bottom-20 left-20 right-20 flex flex-col gap-8"
        >
          <div class="flex justify-between gap-8">
            <RevRating :score="review.averageRate" size="extra-small" />
            <span
              class="text-static-default-hi caption-bold ml-auto mr-0 flex items-center"
            >
              <span>
                {{ review.customer.firstName }}
                {{ review.customer.lastName }}
              </span>
              <IconOfficialFilled class="ml-4" size="16" />
            </span>
          </div>
          <div class="text-static-default-hi caption flex flex-col gap-4">
            <p class="line-clamp-4 w-full whitespace-normal text-start">
              {{ review.comment }}
            </p>
            <RevButtonBase
              ref="reviewButton"
              class="caption-bold w-fit text-start underline"
              variant="primary"
              @click="openModal"
            >
              {{ i18n(translations.viewReview) }}
            </RevButtonBase>
          </div>
        </div>
      </template>

      <template #card-footer>{{
        // Passing empty string to override default slot behaviour
        ''
      }}</template>
    </ReviewCard>
  </div>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, useCssModule, useTemplateRef } from 'vue'

import { formatDifference } from '@backmarket/nuxt-layer-cms/DynamicTextDates.utils'
import ReviewCard from '@backmarket/nuxt-layer-cms/ReviewCard.vue'
import type { Review } from '@backmarket/nuxt-layer-cms/ReviewsView.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevRating } from '@ds/components/Rating'
import { RevTag } from '@ds/components/Tag'
import { IconOfficialFilled } from '@ds/icons/IconOfficialFilled'
import { useIntersectionObserver } from '@vueuse/core'

import { ALT_IMG_SIZES_LARGE, MAIN_IMG_SIZES } from '../../GalleryNew.constants'

import translations from './Review.translations'

const { review, isInMainContainer = false } = defineProps<{
  review: Review
  defer?: boolean
  cardClassName?: string | string[]
  isInMainContainer?: boolean
  isMobile?: boolean
}>()

const emit = defineEmits<{
  (e: 'visible'): void
}>()

const reviewCard = useTemplateRef('reviewCard')
defineExpose({
  openModal: () => reviewCard.value?.reviewCard?.$el.click(),
})

const { trackModal } = useTracking()

function trackReviewClick() {
  trackModal({
    name: 'REVIEW_IN_IMAGE_CAROUSEL',
    status: 'opened',
  })
}

const i18n = useI18n()
const styles = useCssModule()

const currentLocale = useI18nLocale()

const reviewDate = computed(() => {
  const differenceInDays =
    (new Date(review.reviewDate).getTime() - new Date().getTime()) /
    (1000 * 60 * 60 * 24)

  return formatDifference(differenceInDays, currentLocale)
})

const reviewContainer = useTemplateRef('reviewContainer')
const { stop } = useIntersectionObserver(
  reviewContainer,
  ([{ isIntersecting, intersectionRatio }]) => {
    if (isIntersecting && intersectionRatio >= 0.7) {
      emit('visible')
      stop()
    }
  },
  {
    threshold: 0.7,
  },
)

onUnmounted(() => {
  stop()
})
</script>

<style lang="postcss" module>
.cardClass {
  width: 100%;
  margin: auto;
  cursor: pointer;
}

.reviewCardPicture {
  margin: auto;
}

.defaultOverlayClass {
  background: linear-gradient(
    180deg,
    rgba(15, 17, 23, 0) 0%,
    rgba(15, 17, 23, 0.56) 30.29%,
    rgba(15, 17, 23, 0.6) 100%
  );
  height: 50%;
  bottom: 0;
  top: initial !important;
}

.defaultOverlayClass::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: initial !important;
  backdrop-filter: blur(16px);
  /* Mask for the blur effect, so the blur effect is applied smoothly.
  We use a linear-gradient to mask the blur effect */
  mask: linear-gradient(180deg, transparent 20%, #000 50%, #000 100%);
  pointer-events: none;
  border-bottom-left-radius: 0.75rem;
  border-bottom-right-radius: 0.75rem;
  height: 100%;
}
</style>
